import React, { useCallback, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { reconnectWithLatestAuth } from '../app/connectService';
import { store } from '../app/store';
import api from '../common/api';
import { getAndClearLastLocation, isLoginUrl } from '../common/auth-redirect';
import { loadFacilities } from '../feature/facilitiesSlice';
import { ITokenResponse } from '../models/auth';
import authService from '../auth/auth-service';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import Alerts from '../alert/Alerts';
import { useAlerts } from '../alert/AlertContext';

const emailValid = (value: string): boolean => {
  if (!value.trim()) {
    return false;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

const getLoginErrorMessage = (err: unknown): string => {
  const errorObject = err as {
    data?: { error?: string | { error_description?: string }; error_description?: string };
    error?: string;
  };
  const apiError = errorObject?.data?.error;
  if (typeof apiError === 'object' && apiError != null && typeof apiError.error_description === 'string') {
    return apiError.error_description;
  }
  if (typeof errorObject?.data?.error_description === 'string') {
    return errorObject.data.error_description;
  }
  if (apiError === 'invalid_client' || errorObject?.error === 'invalid_client') {
    return 'Credentials are invalid';
  }
  return 'Error while logging in';
};

const LoginPage: React.FC = () => {
  const alerts = useAlerts();
  const [emailAddress, setEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useDocumentTitle('Login');

  const successHandler = useCallback(
    (data: ITokenResponse) => {
      setLoggingIn(false);
      if (data.data.error != null) {
        setLoginError(getLoginErrorMessage({ data: { error: data.data.error } }));
        return;
      }

      console.log(`Login was successful for: ${data.data.staffUser.emailAddress}`);
      authService.storeLogin(data);
      reconnectWithLatestAuth();
      store.dispatch(loadFacilities);

      const lastLocation = getAndClearLastLocation();
      if (lastLocation != null && !isLoginUrl(lastLocation)) {
        console.log(`Redirecting to lastLocation after login: ${lastLocation}`);
        window.location.href = lastLocation;
      } else {
        console.log('No valid lastLocation, redirecting to default state "/"');
        window.location.href = '/';
      }
    },
    []
  );

  const errorHandler = useCallback((err: unknown) => {
    setLoggingIn(false);
    setLoginError(getLoginErrorMessage(err));
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setLoginError(null);
    if (!emailAddress.trim() || !password) {
      return;
    }
    if (!emailValid(emailAddress)) {
      return;
    }

    setLoggingIn(true);
    authService.login(emailAddress, password).then(successHandler, errorHandler);
  };

  const handleRequestReset = () => {
    if (!emailValid(emailAddress)) {
      return;
    }
    setResettingPassword(true);
    api
      .post('/staff-user/password/reset', { emailAddress })
      .then(() => {
        setResettingPassword(false);
        alerts.success('An email was sent to reset your password');
      })
      .catch(() => {
        alerts.error('An error occurred while requesting a password reset email');
        setResettingPassword(false);
      });
  };

  const emailOk = emailValid(emailAddress);
  const showEmailError = submitted && (!emailAddress.trim() || !emailOk);
  const showPasswordError = submitted && !password;

  return (
    <div id="main" role="main">
      <form className="lockscreen" onSubmit={handleLogin} noValidate>
        <div className="logo">
          <h1 className="semi-bold">
            <img src="img/logo-o.png" alt="" /> Relcore&nbsp;
            <small>{import.meta.env.VITE_APP_VERSION ?? 'unknown'}</small>
          </h1>
        </div>
        <div>
          <h1>
            <small>
              <i className="fas fa-lock text-muted"></i> &nbsp;Locked
            </small>
          </h1>

          <Alerts />

          {loginError != null && (
            <Alert variant="danger">{loginError}</Alert>
          )}

          <div className={`form-group${showEmailError ? ' has-error' : ''}`}>
            <input
              type="email"
              name="emailAddress"
              placeholder="Email Address"
              className="form-control"
              value={emailAddress}
              onChange={(ev) => setEmailAddress(ev.target.value)}
              autoComplete="username"
            />
            {showEmailError && (
              <span className="help-block text-danger">
                {!emailAddress.trim() ? 'This is required' : 'Invalid email address'}
              </span>
            )}
          </div>

          <div className={`form-group${showPasswordError ? ' has-error' : ''} mt-1`}>
            <div className="input-group">
              <input
                type="password"
                name="password"
                placeholder="Password"
                className="form-control"
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                autoComplete="current-password"
              />
              <div className="input-group-btn">
                <button className="btn btn-primary" type="submit" disabled={loggingIn}>
                  <i className="fas fa-key"></i> Login
                </button>
              </div>
            </div>
            {showPasswordError && <span className="help-block text-danger">This is required</span>}
          </div>

          {emailOk && (
            <center className="mt-3">
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleRequestReset}
                disabled={resettingPassword}>
                Request Password Reset Email
                <br />
                <small>{emailAddress}</small>
              </button>
            </center>
          )}
        </div>
      </form>
    </div>
  );
};

export default LoginPage;
